<template>
<div class="store-overlay">
<div class="store-modal">
<h2>Tu sucursal</h2>

<!-- LOADING -->
<p v-if="loading" class="loading">
Detectando tu ubicación…
</p>

<!-- MENSAJE -->
<p v-else class="subtitle">
{{ subtitleMessage }}
</p>

<!-- =========================
SUCURSAL RECOMENDADA
========================== -->
<div
v-if="recommendedStore && !showAll"
class="recommended-card"
>
<div class="store-main">
<strong>{{ recommendedStore.name }}</strong>
<div class="small">
{{ recommendedStore.city }}, {{ recommendedStore.zip }}
</div>
</div>

<span class="badge">RECOMENDADA</span>
</div>

<!-- CONFIRMAR (BLOQUEADO) -->
<button
v-if="recommendedStore && !showAll"
class="primary-btn"
@click.prevent
>
Usar esta sucursal
</button>

<!-- CAMBIAR (BLOQUEADO) -->
<button
v-if="stores.length > 1"
class="secondary-btn"
@click.prevent
>
{{ showAll ? "Cancelar" : "Cambiar sucursal" }}
</button>

<!-- =========================
BUSCADOR
========================== -->
<input
v-if="showAll"
v-model="search"
class="search"
placeholder="Buscar por ciudad o código postal"
/>

<!-- =========================
LISTA (BLOQUEADA)
========================== -->
<ul v-if="showAll">
<li
v-for="store in filteredStores"
:key="store.id"
@click.prevent
>
<div>
📍 {{ store.name }}
<div class="small">
{{ store.city }}, {{ store.zip }}
</div>
</div>
</li>
</ul>
</div>
</div>
</template>

<script setup>
import { ref, onMounted, computed } from "vue";
import axios from "axios";
import baseUrl from "@/api";
import { useLocation } from "@/components/composables/uselocation";
import { useLocationStore } from "@/stores/location";

const locationStore = useLocationStore();

const loading = ref(true);
const stores = ref([]);
const showAll = ref(false);
const search = ref("");
const locationGranted = ref(false);

const { getUserLocation } = useLocation();

/* ======================
DISTANCIA (Haversine)
====================== */
function distance(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;

  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) *
    Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;

  return R * (2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)));
}

/* ======================
COMPUTEDS
====================== */
const recommendedStore = computed(() =>
  stores.value.find(s => s.recommended)
);

const filteredStores = computed(() => {
  const q = search.value.toLowerCase();
  return stores.value.filter(s =>
    `${s.name} ${s.city} ${s.zip}`.toLowerCase().includes(q)
  );
});

const subtitleMessage = computed(() => {
  if (loading.value) return "";

  if (!locationGranted.value) {
    return "No pudimos detectar tu ubicación. Selecciona la sucursal más cercana.";
  }

  if (recommendedStore.value && !showAll.value) {
    return "Detectamos automáticamente la sucursal más cercana a tu ubicación.";
  }

  return "Selecciona la sucursal donde deseas comprar.";
});

/* ======================
MOUNTED
====================== */
onMounted(async () => {
  try {
    const res = await axios.get(`${baseUrl}/shopify/locations`);
    const data = res.data || [];

    if (data.length === 1) {
      locationStore.setStore(data[0]);
      return;
    }

    stores.value = data.map(s => ({
      ...s,
      recommended: false
    }));

    try {
      const { lat, lng } = await getUserLocation(5000);
      locationGranted.value = true;

      let nearest = null;
      let min = Infinity;

      stores.value.forEach(s => {
        if (!s.lat || !s.lng) return;
        const d = distance(lat, lng, s.lat, s.lng);
        if (d < min) {
          min = d;
          nearest = s;
        }
      });

      if (nearest) nearest.recommended = true;

    } catch {
      locationGranted.value = false;
      showAll.value = true;
    }

  } finally {
    loading.value = false;
  }
});
</script>
